<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import ContactNotesAPI from 'dashboard/api/contactNotes';
import ConversationNotesAPI from 'dashboard/api/conversationNotes';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  notes: {
    type: Object,
    default: () => ({ contact_notes: [], conversation_notes: [] }),
  },
});

const emit = defineEmits(['refresh']);

const store = useStore();

const isLoading = ref(false);
const showForm = ref(false);
const noteType = ref('contact');
const editingNote = ref(null);
const formData = ref({
  content: '',
  note_type: 'general',
  is_private: false,
});

const currentUser = computed(() => store.getters.getCurrentUser);

const noteTypeOptions = [
  { value: 'general', label: 'Geral', icon: 'i-lucide-sticky-note' },
  { value: 'important', label: 'Importante', icon: 'i-lucide-alert-triangle' },
  { value: 'follow_up', label: 'Follow-up', icon: 'i-lucide-refresh-cw' },
  { value: 'internal', label: 'Interno', icon: 'i-lucide-lock' },
];

const contactNotes = computed(() => props.notes?.contact_notes || []);
const conversationNotes = computed(() => props.notes?.conversation_notes || []);
const allNotes = computed(() => {
  const all = [
    ...contactNotes.value.map(n => ({ ...n, source: 'contact' })),
    ...conversationNotes.value.map(n => ({ ...n, source: 'conversation' })),
  ];
  return all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

const formatDate = dateString => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getNoteTypeInfo = type => {
  return noteTypeOptions.find(n => n.value === type) || noteTypeOptions[0];
};

const resetForm = () => {
  formData.value = { content: '', note_type: 'general', is_private: false };
  editingNote.value = null;
  noteType.value = 'contact';
};

const openForm = (note = null) => {
  if (note) {
    editingNote.value = note;
    noteType.value =
      note.source || (note.conversation_id ? 'conversation' : 'contact');
    formData.value = {
      content: note.content,
      note_type: note.note_type || 'general',
      is_private: note.is_private || false,
    };
  } else {
    resetForm();
  }
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const handleSave = async () => {
  if (!formData.value.content.trim()) {
    useAlert('Digite o conteúdo da nota');
    return;
  }

  isLoading.value = true;
  try {
    if (noteType.value === 'contact') {
      if (editingNote.value) {
        await ContactNotesAPI.update(
          props.contactId,
          editingNote.value.id,
          formData.value.content
        );
      } else {
        await ContactNotesAPI.create(props.contactId, formData.value.content);
      }
    } else {
      if (!props.conversationId) {
        useAlert('Selecione uma conversa para adicionar notas');
        isLoading.value = false;
        return;
      }
      if (editingNote.value) {
        await ConversationNotesAPI.update(
          props.conversationId,
          editingNote.value.id,
          formData.value
        );
      } else {
        await ConversationNotesAPI.create(props.conversationId, formData.value);
      }
    }
    useAlert('Nota salva!');
    closeForm();
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao salvar nota');
  } finally {
    isLoading.value = false;
  }
};

const handleDelete = async note => {
  if (!confirm('Excluir esta nota?')) return;
  try {
    if (note.source === 'contact' || !note.conversation_id) {
      await ContactNotesAPI.delete(props.contactId, note.id);
    } else {
      await ConversationNotesAPI.delete(note.conversation_id, note.id);
    }
    useAlert('Nota excluída!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao excluir nota');
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        Notas ({{ allNotes.length }})
      </h3>
      <Button
        label="Nova Nota"
        icon="i-lucide-plus"
        size="sm"
        @click="openForm()"
      />
    </div>

    <!-- Stats -->
    <div class="flex gap-3">
      <div
        class="px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20"
      >
        <span class="text-xs font-medium text-blue-600">
          {{ contactNotes.length }} notas do contato
        </span>
      </div>
      <div
        class="px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-900/20"
      >
        <span class="text-xs font-medium text-purple-600">
          {{ conversationNotes.length }} notas de conversas
        </span>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="allNotes.length === 0"
      class="flex flex-col items-center justify-center py-12 rounded-xl bg-n-alpha-1"
    >
      <span class="i-lucide-sticky-note size-12 text-n-slate-10 opacity-50" />
      <p class="mt-4 text-sm text-n-slate-11">Nenhuma nota cadastrada</p>
    </div>

    <!-- Notes List -->
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="note in allNotes"
        :key="`${note.source}-${note.id}`"
        class="p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
        :class="{
          'border-l-4 border-l-amber-400': note.note_type === 'important',
        }"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-2 flex-wrap">
              <span
                class="px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  note.source === 'contact'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-purple-100 text-purple-700'
                "
              >
                {{ note.source === 'contact' ? 'Contato' : 'Conversa' }}
              </span>
              <span
                v-if="note.note_type && note.note_type !== 'general'"
                class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-alpha-2 text-n-slate-11"
              >
                {{ getNoteTypeInfo(note.note_type).label }}
              </span>
              <span
                v-if="note.is_private"
                class="px-2 py-0.5 rounded-full text-xs font-medium bg-ruby-100 text-ruby-700"
              >
                Privado
              </span>
            </div>
            <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
              {{ note.content }}
            </p>
            <div class="flex items-center gap-4 mt-3 text-xs text-n-slate-10">
              <span>{{ formatDate(note.created_at) }}</span>
              <span v-if="note.user">por {{ note.user.name }}</span>
            </div>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="openForm(note)"
            >
              <span class="i-lucide-pencil size-4" />
            </button>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-ruby-600 hover:bg-ruby-500/10 transition-colors"
              @click="handleDelete(note)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Modal -->
    <Teleport to="body">
      <div
        v-if="showForm"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black5 backdrop-blur-sm"
          @click="closeForm"
        />
        <div
          class="relative w-full max-w-lg overflow-hidden rounded-2xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          >
            <h3 class="text-base font-medium text-n-slate-12">
              {{ editingNote ? 'Editar Nota' : 'Nova Nota' }}
            </h3>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="closeForm"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="p-6 flex flex-col gap-4">
            <!-- Note Type Selection -->
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-11">
                Vincular a:
              </label>
              <div class="flex gap-2">
                <button
                  class="flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  :class="
                    noteType === 'contact'
                      ? 'border-woot-500 bg-woot-500/10 text-woot-600'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  @click="noteType = 'contact'"
                >
                  <span class="i-lucide-user size-4" />
                  Contato
                </button>
                <button
                  class="flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  :class="
                    noteType === 'conversation'
                      ? 'border-woot-500 bg-woot-500/10 text-woot-600'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  :disabled="!conversationId"
                  @click="noteType = 'conversation'"
                >
                  <span class="i-lucide-message-square size-4" />
                  Conversa
                </button>
              </div>
            </div>

            <!-- Note Type (for conversation notes) -->
            <div v-if="noteType === 'conversation'" class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-11">
                Tipo da Nota
              </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="opt in noteTypeOptions"
                  :key="opt.value"
                  class="px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors flex items-center gap-1.5"
                  :class="
                    formData.note_type === opt.value
                      ? 'border-woot-500 bg-woot-500/10 text-woot-600'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  @click="formData.note_type = opt.value"
                >
                  <span :class="opt.icon" class="size-3.5" />
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <!-- Content -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">
                Conteúdo *
              </label>
              <textarea
                v-model="formData.content"
                rows="5"
                placeholder="Digite sua nota aqui..."
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors resize-none"
              />
            </div>

            <!-- Private checkbox (for conversation notes) -->
            <label
              v-if="noteType === 'conversation'"
              class="inline-flex items-center gap-2 cursor-pointer"
            >
              <input
                v-model="formData.is_private"
                type="checkbox"
                class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 focus:ring-offset-0"
              />
              <span class="text-sm text-n-slate-12">
                Nota privada (visível apenas para você)
              </span>
            </label>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
          >
            <Button
              label="Cancelar"
              variant="ghost"
              color="slate"
              @click="closeForm"
            />
            <Button
              :label="isLoading ? 'Salvando...' : 'Salvar'"
              :is-loading="isLoading"
              :disabled="isLoading"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
